import { TextField, InputLabel, Select, MenuItem, Card, CardContent, Typography, Button, Box, Container, Fade, CircularProgress, Divider, IconButton, InputAdornment, Grid, Avatar, Checkbox, FormControlLabel, Alert, useMediaQuery, CssBaseline } from '@mui/material';
import { 
  Email as EmailIcon,
  Lock as LockIcon,
  Person as PersonIcon,
  LocationOn as LocationIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  CalendarToday as CalendarIcon,
  CheckCircle as CheckCircleIcon,
  PhotoCamera as PhotoCameraIcon,
  Close as CloseIcon,
  Login as LoginIcon,
  LockReset as LockResetIcon,
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
} from "@mui/icons-material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { useContext, useState, useEffect, useMemo, useCallback, useRef } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { SupabaseUserContext } from "../contexts/supabaseUser.context";
import InputValidator from "../utils/security/inputValidator";

import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { format } from "date-fns";
import { ThemeProvider as CustomThemeProvider, useTheme } from "../contexts/ThemeContext";
import AuthPageToggleBar from "../components/AuthPageToggleBar";
import AuthPageBottomAnimation from "../components/AuthPageBottomAnimation";
import SignupRoleSelector from "../components/SignupRoleSelector";
import SignupStepTransition, { SignupStepIndicator } from "../components/SignupStepTransition";
import { supabase } from "../supabase/client";
import { compressLogoImage } from "../utils/utils/imageUtils";
import { uploadUserProfileSignup } from "../utils/utils/supabaseStorage";
import { getBeltDisplayName } from "../utils/utils/membersBeltUtils";
import { buildSignupSportSelectOptions, sportHasBeltRank, validationBeltSystemKeyFromGroup } from "../utils/beltSystemCatalog";
import { getSignupAdultBeltOrder, resolvePromotionBeltPickerValue } from "../utils/beltCatalog";
import { PromotionBeltPickerField } from "../utils/components/belt/BeltRankVisual";
import {
  saveSignupPendingProfile,
  validateSignupPersonnummer,
  fetchMyRegistrationStatus,
  fetchSignupClubs,
  resolvePublicSignupInvite,
} from "../utils/api/api";
import { isPersonnummerPlausible } from "../utils/swedishPersonnummer";
import {
  getSignupInviteTokenFromSearch,
  parseLegacySignupInviteFromSearch,
} from "../utils/signupInviteLink";
import tokenManager from "../utils/security/tokenManager";
import { useTranslation } from "react-i18next";
import { SnackbarNotification } from "../utils/components/SnackbarNotification";
import {
  authAlertWarningSx,
  authAlertInfoSx,
} from "../utils/authPageStyles";
import {
  SIGNUP_FONT,
  signupPageOuterSx,
  signupPageContainerSx,
  signupContentColumnSx,
  signupCardShellSx,
  signupFieldSx,
  signupDateFieldSx,
  signupSelectSx,
  signupPrimaryButtonSx,
  signupOutlineButtonSx,
  signupAlertSx,
  signupSummaryBoxSx,
  signupLogoSx,
  signupTypography,
  signupCardContentSx,
  signupFormGridSx,
} from "../utils/signupPageStyles";
import { BRAND_PRIMARY } from "../constants/brandColors";
import { getMm } from "../theme/modernMaterialEnterprise";

const SignupContent = () => {
  const { isDarkMode } = useTheme();
  const { t } = useTranslation();
  const fieldSx = useMemo(() => signupFieldSx(isDarkMode), [isDarkMode]);
  const selectSx = useMemo(() => signupSelectSx(isDarkMode), [isDarkMode]);
  const cardShellSx = useMemo(() => signupCardShellSx(isDarkMode), [isDarkMode]);
  const logoSx = useMemo(() => signupLogoSx(), []);
  const typeSx = useMemo(() => signupTypography(isDarkMode), [isDarkMode]);
  const primaryBtnSx = useMemo(() => signupPrimaryButtonSx(isDarkMode), [isDarkMode]);
  const outlineBtnSx = useMemo(() => signupOutlineButtonSx(isDarkMode), [isDarkMode]);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "error" });
  const [role, setRole] = useState(""); // State to track selected role (Parent or Student)
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [loadingStep, setLoadingStep] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [imagePreview, setImagePreview] = useState(null);
  const [isCompressing, setIsCompressing] = useState(false);
  const [showEmailConfirmationPopup, setShowEmailConfirmationPopup] = useState(false);
  const [instructionsAcknowledged, setInstructionsAcknowledged] = useState(false);
  const [duplicateEmailGuidance, setDuplicateEmailGuidance] = useState(false);
  const [clubs, setClubs] = useState([]);
  const [clubsLoading, setClubsLoading] = useState(true);
  const [clubsError, setClubsError] = useState("");
  const [inviteClubLocked, setInviteClubLocked] = useState(false);
  const [inviteRoleLocked, setInviteRoleLocked] = useState(false);
  const [inviteLinkInvalid, setInviteLinkInvalid] = useState(false);
  const [wizardStep, setWizardStep] = useState(0);
  const [slideDirection, setSlideDirection] = useState("forward");

  const showRoleStep = !inviteRoleLocked;
  const wizardStepKeys = useMemo(
    () => (showRoleStep ? ["role", "instructions", "form"] : ["instructions", "form"]),
    [showRoleStep]
  );
  const wizardStepLabels = useMemo(
    () =>
      wizardStepKeys.map((key) => {
        if (key === "role") return t("signup_step_role");
        if (key === "instructions") return t("signup_step_instructions");
        return t("signup_step_form");
      }),
    [wizardStepKeys, t]
  );
  const currentWizardKey = wizardStepKeys[wizardStep] || wizardStepKeys[0];

  const goToWizardStep = useCallback(
    (nextStep, direction = "forward") => {
      setSlideDirection(direction);
      setWizardStep(Math.max(0, Math.min(nextStep, wizardStepKeys.length - 1)));
    },
    [wizardStepKeys.length]
  );

  const goNextWizardStep = useCallback(() => {
    goToWizardStep(wizardStep + 1, "forward");
  }, [goToWizardStep, wizardStep]);

  const goBackWizardStep = useCallback(() => {
    goToWizardStep(wizardStep - 1, "back");
  }, [goToWizardStep, wizardStep]);

  const wizardAnchorRef = useRef(null);
  const prevWizardStepRef = useRef(wizardStep);
  const isMobileLayout = useMediaQuery("(max-width:899px)");

  useEffect(() => {
    const prevStep = prevWizardStepRef.current;
    prevWizardStepRef.current = wizardStep;

    if (!isMobileLayout || wizardStep <= prevStep) return;

    requestAnimationFrame(() => {
      wizardAnchorRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    });
  }, [wizardStep, isMobileLayout]);

  // Theme is now handled by our ThemeContext

  const navigate = useNavigate();
  const location = useLocation();

  const { emailPasswordSignup, emailPasswordLogin } = useContext(SupabaseUserContext);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setClubsLoading(true);
      setClubsError("");
      try {
        const list = await fetchSignupClubs();
        if (!cancelled) setClubs(Array.isArray(list) ? list : []);
      } catch (err) {
        console.error("Failed to load signup clubs", err);
        if (!cancelled) {
          setClubsError("Could not load academies. Please refresh and try again.");
          setClubs([]);
        }
      } finally {
        if (!cancelled) setClubsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const token = getSignupInviteTokenFromSearch(location.search);
    if (!token) return undefined;
    let cancelled = false;
    (async () => {
      try {
        const data = await resolvePublicSignupInvite(token);
        if (cancelled) return;
        setForm((prev) => ({
          ...prev,
          clubID: String(data.clubID),
          clubname: data.clubname || "",
          belt: data.role === "Parent" ? "" : prev.belt,
          childName: data.role === "Student" ? "" : prev.childName,
        }));
        setInviteClubLocked(true);
        setInviteLinkInvalid(false);
        if (data.role) {
          setRole(data.role);
          setInviteRoleLocked(true);
        } else {
          setInviteRoleLocked(false);
        }
      } catch {
        if (!cancelled) {
          setInviteLinkInvalid(true);
          setInviteClubLocked(false);
          setInviteRoleLocked(false);
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [location.search]);

  useEffect(() => {
    if (getSignupInviteTokenFromSearch(location.search)) return;
    if (clubsLoading) return;
    const { clubID: inviteClubId, role: inviteRole } = parseLegacySignupInviteFromSearch(location.search);

    let clubOk = true;
    if (inviteClubId != null) {
      const match = clubs.find((c) => String(c.clubID) === String(inviteClubId));
      if (match) {
        setForm((prev) => ({
          ...prev,
          clubID: String(match.clubID),
          clubname: match.clubname || "",
        }));
        setInviteClubLocked(true);
      } else {
        clubOk = false;
        setInviteClubLocked(false);
      }
    } else {
      setInviteClubLocked(false);
    }

    const roleParamPresent = /[?&](r|role)=/i.test(location.search);
    if (inviteRole && (inviteClubId == null || clubOk)) {
      setRole(inviteRole);
      setInviteRoleLocked(true);
      setForm((prev) => ({
        ...prev,
        belt: inviteRole === "Parent" ? "" : prev.belt,
        childName: inviteRole === "Student" ? "" : prev.childName,
      }));
    } else {
      setInviteRoleLocked(false);
    }

    setInviteLinkInvalid(
      (inviteClubId != null && !clubOk) || (roleParamPresent && !inviteRole)
    );
  }, [clubsLoading, clubs, location.search]);

  useEffect(() => {
    setWizardStep((prev) => Math.min(prev, Math.max(wizardStepKeys.length - 1, 0)));
  }, [wizardStepKeys.length]);

  // Function to handle image upload and compression
  const handleImageChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setSnackbar(s => ({ ...s, open: false }));
    
    // Allowed image types
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    const maxSize = 10 * 1024 * 1024; // 10MB

    // Validate file type
    if (!allowedTypes.includes(file.type)) {
      const allowedExtensions = allowedTypes.map(type => type.split('/')[1].toUpperCase()).join(', ');
      setSnackbar({ open: true, message: `${t("signup_invalid_file_type")} ${allowedExtensions}`, severity: "error" });
      // Clear file input
      e.target.value = '';
      setSelectedFile(null);
      setImagePreview(null);
      setForm(prev => ({ ...prev, profileImage: '' }));
      return;
    }

    // Validate file size
    if (file.size > maxSize) {
      const maxSizeMB = Math.round(maxSize / (1024 * 1024));
      const fileSizeMB = (file.size / (1024 * 1024)).toFixed(2);
      setSnackbar({ open: true, message: `File size too large. Maximum size: ${maxSizeMB}MB. Your file is ${fileSizeMB}MB.`, severity: "error" });
      // Clear file input
      e.target.value = '';
      setSelectedFile(null);
      setImagePreview(null);
      setForm(prev => ({ ...prev, profileImage: '' }));
      return;
    }

    // File validation passed - proceed with preview and compression
    setSelectedFile(file);
    setSnackbar(s => ({ ...s, open: false }));

    // Create preview
    const reader = new FileReader();
    reader.onload = () => setImagePreview(reader.result);
    reader.readAsDataURL(file);

    // Compress image
    setIsCompressing(true);
    try {
      const compressedBase64 = await compressLogoImage(file);
      if (compressedBase64) {
        setForm(prev => ({ ...prev, profileImage: compressedBase64 }));
      } else {
        setSnackbar({ open: true, message: t("signup_failed_compress"), severity: "error" });
        // Clear state on compression failure
        e.target.value = '';
        setSelectedFile(null);
        setImagePreview(null);
        setForm(prev => ({ ...prev, profileImage: '' }));
      }
    } catch (error) {
      console.error('❌ Image compression failed:', error);
      setSnackbar({ open: true, message: 'Failed to compress image. Please try another image.', severity: "error" });
      // Clear state on compression failure
      e.target.value = '';
      setSelectedFile(null);
      setImagePreview(null);
      setForm(prev => ({ ...prev, profileImage: '' }));
    } finally {
      setIsCompressing(false);
    }
  };

  /** Block re-registration when this email is already a member or pending at the selected club. */
  const checkEmailRegisteredForClub = async (emailLower, clubID) => {
    if (!emailLower || clubID == null || clubID === "") {
      return { inUsers: false, inPending: false };
    }
    const clubIdValue = Number(clubID);
    const clubFilter = Number.isNaN(clubIdValue) ? clubID : clubIdValue;

    const [usersRes, pendingRes] = await Promise.all([
      supabase
        .from("users")
        .select("studentID")
        .eq("email", emailLower)
        .eq("clubID", clubFilter)
        .maybeSingle(),
      supabase
        .from("pending_users")
        .select("studentID")
        .eq("email", emailLower)
        .eq("clubID", clubFilter)
        .maybeSingle(),
    ]);

    return {
      inUsers: !!usersRes.data,
      inPending: !!pendingRes.data,
    };
  };

  const [form, setForm] = useState({
    name: "",
    personnummer: "",
    email: "",
    password: "",
    clubID: "",
    clubname: "",
    childName: "",
    belt: "",
    sportKey: "",
    address: "",
    smoothcompId: "",
    profileImage: "",
  });

  const onFormInputChange = (event) => {
    const { name, value } = event.target;
    setForm((prevForm) => ({
      ...prevForm,
      [name]: value,
    }));
  };

  const handleClubChange = (event) => {
    const selectedValue = event.target.value;
    const selected = clubs.find(
      (c) => String(c.clubID) === String(selectedValue)
    );
    setForm((prev) => ({
      ...prev,
      clubID: selectedValue,
      clubname: selected?.clubname || "",
      sportKey: "",
      belt: "",
    }));
  };

  const selectedClub = useMemo(
    () => clubs.find((c) => String(c.clubID) === String(form.clubID)),
    [clubs, form.clubID]
  );

  const signupSportOptions = useMemo(
    () => buildSignupSportSelectOptions(selectedClub?.selectableSportKeys),
    [selectedClub]
  );

  const isStudentSignupRole = role === "Student" || role === "student&Parent";
  const isParentSignupRole = role === "Parent" || role === "student&Parent";
  const isParentOnlyRole = role === "Parent";
  const signupPersonnummerRequired = !isParentOnlyRole;
  const selectedSportRequiresBelt =
    isStudentSignupRole && form.sportKey && sportHasBeltRank(form.sportKey);
  const showSportField = isStudentSignupRole && Boolean(form.clubID) && signupSportOptions.length > 0;
  const showBeltField = Boolean(selectedSportRequiresBelt);

  const signupBeltSystemKey = useMemo(
    () => validationBeltSystemKeyFromGroup({ sportKey: form.sportKey }),
    [form.sportKey]
  );

  const signupBeltOrder = useMemo(
    () => getSignupAdultBeltOrder(signupBeltSystemKey),
    [signupBeltSystemKey]
  );

  useEffect(() => {
    setForm((prev) => {
      if (!prev.belt || !signupBeltOrder.length) return prev;
      const resolved = resolvePromotionBeltPickerValue(prev.belt, signupBeltOrder);
      if (resolved && signupBeltOrder.includes(resolved)) return prev;
      return { ...prev, belt: "" };
    });
  }, [signupBeltOrder, form.sportKey]);

  useEffect(() => {
    if (!form.clubID || !isStudentSignupRole) return;
    if (signupSportOptions.length !== 1) return;
    const only = signupSportOptions[0];
    setForm((prev) => {
      if (prev.sportKey === only.value) return prev;
      return {
        ...prev,
        sportKey: only.value,
        belt: only.hasBeltRank ? prev.belt : "",
      };
    });
  }, [form.clubID, isStudentSignupRole, signupSportOptions]);

  const handleSportChange = (event) => {
    const sportKey = event.target.value;
    setForm((prev) => ({
      ...prev,
      sportKey,
      belt: sportHasBeltRank(sportKey) ? prev.belt : "",
    }));
  };

  const handleSignupBeltChange = (belt) => {
    setForm((prev) => ({ ...prev, belt }));
  };

  const redirectNow = () => {
    const redirectTo = location.search.replace("?redirectTo=", "");
    navigate(redirectTo ? redirectTo : "/");
  };

  // Note: Email notifications are now handled by the backend
  // The backend will automatically send admin notifications and user confirmations
  const handleUserRegistration = async (name, email, clubname) => {
    // Registration logic without direct email sending
    // Backend will handle all email notifications automatically
  };

  /** Same rules as submit — drives disabled state on “Create account”. */
  const signupSubmitReady = useMemo(() => {
    if (isLoading || isCompressing) return false;

    if (!role) return false;
    if (!form.name?.trim() || !InputValidator.validateName(form.name).isValid) return false;
    if (!InputValidator.validateEmail(form.email).isValid) return false;
    if (!InputValidator.validatePassword(form.password).isValid) return false;
    if (!form.clubID) return false;

    if (signupPersonnummerRequired) {
      const pn = form.personnummer?.trim();
      if (!pn || !isPersonnummerPlausible(pn)) return false;
    } else if (form.personnummer?.trim() && !isPersonnummerPlausible(form.personnummer)) {
      return false;
    }

    if (isParentSignupRole && !form.childName?.trim()) return false;

    if (isStudentSignupRole) {
      if (signupSportOptions.length > 0 && !form.sportKey?.trim()) return false;
      if (form.sportKey && sportHasBeltRank(form.sportKey) && !form.belt?.trim()) return false;
    }

    if (!form.profileImage || !selectedFile) return false;

    return true;
  }, [
    form,
    role,
    selectedFile,
    isLoading,
    isCompressing,
    isStudentSignupRole,
    isParentSignupRole,
    signupPersonnummerRequired,
    signupSportOptions,
  ]);

  const onSubmit = async () => {
    if (form.personnummer?.trim()) {
      if (!isPersonnummerPlausible(form.personnummer)) {
        setSnackbar({ open: true, message: t("signup_personnummer_invalid"), severity: "error" });
        return;
      }
    } else if (signupPersonnummerRequired) {
      setSnackbar({ open: true, message: t("signup_personnummer_invalid"), severity: "error" });
      return;
    }

    // Check name validation early to provide specific error message
    if (form.name) {
      const nameValidation = InputValidator.validateName(form.name);
      if (!nameValidation.isValid) {
        setSnackbar({ open: true, message: nameValidation.error, severity: "error" });
        return;
      }
    }

    if (isParentSignupRole && !form.childName?.trim()) {
      setSnackbar({
        open: true,
        message: t("signup_child_name_required"),
        severity: "error",
      });
      return;
    }

    if (!signupSubmitReady) {
      if (!form.profileImage || !selectedFile) {
        setSnackbar({ open: true, message: t("signup_complete_fields"), severity: "error" });
      } else {
        setSnackbar({ open: true, message: t("signup_complete_fields_no_picture"), severity: "error" });
      }
      return;
    }

    setIsLoading(true);
    setSnackbar(s => ({ ...s, open: false })); // Clear any previous errors
    setDuplicateEmailGuidance(false);

    try {
      // Step 1: Input validation
      setLoadingStep('validating');
      const emailValidation = InputValidator.validateEmail(form.email);
      const passwordValidation = InputValidator.validatePassword(form.password);
      const nameValidation = InputValidator.validateName(form.name);

      if (!emailValidation.isValid) {
        setSnackbar({ open: true, message: emailValidation.error, severity: "error" });
        return;
      }

      if (!passwordValidation.isValid) {
        setSnackbar({ open: true, message: passwordValidation.error, severity: "error" });
        return;
      }

      if (!nameValidation.isValid) {
        setSnackbar({ open: true, message: nameValidation.error, severity: "error" });
        return;
      }

      if (!form.clubID) {
        setSnackbar({ open: true, message: t("signup_complete_fields"), severity: "error" });
        return;
      }

      if (signupPersonnummerRequired || form.personnummer?.trim()) {
        try {
          await validateSignupPersonnummer(form.personnummer.trim());
        } catch (pnError) {
          const status = pnError.response?.status;
          if (status === 400) {
            const data = pnError.response?.data;
            setSnackbar({
              open: true,
              message: getErrorMessage({
                code: data?.code || "personnummer",
                message: data?.error,
              }),
              severity: "error",
            });
            return;
          }
        }
      }

      const emailLower = emailValidation.value;
      const existingRegistration = await checkEmailRegisteredForClub(emailLower, form.clubID);
      if (existingRegistration.inUsers) {
        setDuplicateEmailGuidance(true);
        setSnackbar({
          open: true,
          message: t("signup_error_email_member_exists"),
          severity: "error",
        });
        return;
      }
      if (existingRegistration.inPending) {
        setDuplicateEmailGuidance(true);
        setSnackbar({
          open: true,
          message: t("signup_error_email_pending_exists"),
          severity: "error",
        });
        return;
      }

      // Step 2: Supabase registration. A leftover login from a failed attempt is reused when the password matches and no club record exists.
      setLoadingStep('registering');
      let signupResult;
      try {
        signupResult = await emailPasswordSignup(
          emailValidation.value, 
          passwordValidation.value
        );
      } catch (supabaseError) {
        console.error('❌ Supabase registration failed:', supabaseError);
        if (supabaseError?.code !== "user_already_registered") {
          setSnackbar({ open: true, message: getErrorMessage(supabaseError), severity: "error" });
          return;
        }
        try {
          await emailPasswordLogin(emailValidation.value, passwordValidation.value);
          const status = await fetchMyRegistrationStatus();
          if (status?.status === "pending_review") {
            setDuplicateEmailGuidance(true);
            setSnackbar({
              open: true,
              message: t("signup_error_email_pending_exists"),
              severity: "error",
            });
            return;
          }
          if (status?.status === "approved") {
            setDuplicateEmailGuidance(true);
            setSnackbar({
              open: true,
              message: t("signup_error_email_member_exists"),
              severity: "error",
            });
            return;
          }
          signupResult = { authUserId: "resumed" };
        } catch (loginErr) {
          console.error("Could not resume unfinished signup:", loginErr);
          setDuplicateEmailGuidance(true);
          setSnackbar({ open: true, message: getErrorMessage(supabaseError), severity: "error" });
          return;
        }
      }

      const authUserId =
        signupResult?.authUserId ||
        signupResult?.user?.id ||
        signupResult?.id;

      if (!authUserId) {
        console.error('❌ Unable to extract user ID from Supabase response:', signupResult);
        setSnackbar({ open: true, message: t("signup_registration_failed"), severity: "error" });
        return;
      }

      // Ensure backend can authenticate this signup (JWT for pending-profile API)
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (session?.access_token) {
          tokenManager.setToken(session.access_token);
        }
      } catch (_) {
        /* non-fatal; saveSignupPendingProfile will surface 401 if needed */
      }

      // Step 3: Save the club application. If it fails and no club record exists, the new login is removed.
      setLoadingStep('creating-profile');
      try {
        const userData = {
          name: form.name,
          personnummer: form.personnummer?.trim() || null,
          address: form.address,
          clubID: form.clubID,
          clubname: form.clubname,
          belt: showBeltField ? form.belt : null,
          sportKey: isStudentSignupRole ? form.sportKey : null,
          child_name: form.childName?.trim() || null,
          isParent: role === "Parent" || role === "student&Parent",
          isStudent: role === "Student" || role === "student&Parent",
        };

        const { pendingStudentID } = await saveSignupPendingProfile(userData);
        
        // Step 4: Upload profile image if provided
        if (form.profileImage && selectedFile) {
          setLoadingStep('uploading-image');
          try {
            // Validate file type one more time before upload
            const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
            const fileType = selectedFile.type;
            const validImageType = allowedTypes.includes(fileType) ? fileType : 'image/png';
            
            const uploadResult = await uploadUserProfileSignup(
              form.profileImage, 
              pendingStudentID, 
              validImageType
            );
            
            if (uploadResult.success) {
              // Image uploaded successfully
            } else {
              // Image upload failed
            }
          } catch (imageError) {
            console.error('Profile image upload error:', imageError.message);
          }
        } else {
        }
      } catch (profileError) {
        console.error('Failed to save pending user profile:', profileError);
        const apiCode = profileError.response?.data?.code;
        const apiError = profileError.response?.data?.error;
        const authRolledBack = profileError.response?.data?.authRolledBack === true;
        if (apiCode === "signup_email_member_exists" || apiCode === "signup_email_pending_exists") {
          setDuplicateEmailGuidance(true);
        }
        if (authRolledBack) {
          try {
            await supabase.auth.signOut({ scope: "local" });
          } catch (_) {
            /* local session may already be gone */
          }
          tokenManager.clearTokens();
        }
        const errForMessage = apiCode
          ? { code: apiCode, message: apiError, response: profileError.response }
          : profileError;
        setSnackbar({
          open: true,
          message: getErrorMessage(errForMessage),
          severity: "error",
        });
        return;
      }

      // Step 5: Success - show email confirmation popup
      setShowEmailConfirmationPopup(true);

    } catch (err) {
      console.error('❌ Unexpected error during registration:', err);
      setSnackbar({ open: true, message: getErrorMessage(err), severity: "error" });
    } finally {
      setIsLoading(false);
      setLoadingStep('');
    }
  };

const handleRoleSelection = (selectedRole) => {
  setRole(selectedRole);
  setInstructionsAcknowledged(false);
  setForm((prevForm) => ({
    ...prevForm,
    belt:
      selectedRole === "Parent"
        ? ""
        : prevForm.belt,
    sportKey: selectedRole === "Parent" ? "" : prevForm.sportKey,
    childName: selectedRole === "Student" ? "" : prevForm.childName,
    personnummer: selectedRole === "Parent" ? "" : prevForm.personnummer,
  }));
};

  const handleTogglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Enhanced error handling (Supabase Auth + HTTP APIs)
  const getErrorMessage = (error) => {
    if (!error) return t("signup_unexpected_error");

    if (error.code === "user_already_registered") {
      return t("signup_error_email_already_exists");
    }

    if (error.code === "signup_email_member_exists") {
      return t("signup_error_email_member_exists");
    }

    if (error.code === "signup_email_pending_exists") {
      return t("signup_error_email_pending_exists");
    }

    if (error.code === "age") {
      return t("signup_personnummer_too_young");
    }

    if (error.code === "checksum" || error.code === "format" || error.code === "personnummer") {
      return t("signup_personnummer_invalid");
    }

    if (error.code === "signup_profile_save_failed") {
      return t("signup_profile_save_failed");
    }

    if (error.code === "signup_sport_required") {
      return t("signup_sport_required");
    }

    if (error.code === "signup_sport_not_allowed") {
      return t("signup_sport_not_allowed");
    }

    if (error.code === "signup_belt_required") {
      return t("signup_belt_required");
    }

    if (error.code === "signup_member_group_required") {
      return t("signup_stale_api_group_rule");
    }

    const rawMsg = (error.message || "").toLowerCase();
    const duplicateHints = [
      "user already registered",
      "already registered",
      "email address is already registered",
      "already been registered",
      "user already exists",
    ];
    if (duplicateHints.some((hint) => rawMsg.includes(hint))) {
      return t("signup_error_email_already_exists");
    }

    if (error.response) {
      const status = error.response.status;
      const data = error.response.data;
      
      switch (status) {
        case 409:
          if (data?.code === "signup_email_member_exists") {
            return t("signup_error_email_member_exists");
          }
          if (data?.code === "signup_email_pending_exists") {
            return t("signup_error_email_pending_exists");
          }
          return t("signup_error_email_already_exists");
        case 400:
          if (data?.code === "signup_member_group_required") {
            return t("signup_stale_api_group_rule");
          }
          if (data?.code === "signup_sport_required") return t("signup_sport_required");
          if (data?.code === "signup_belt_required") return t("signup_belt_required");
          return data.error || "Invalid information provided.";
        case 401:
          return "Authentication failed. Please try again.";
        case 403:
          return "Access denied. Please contact support.";
        case 404:
          return "Service not found. Please try again.";
        case 500:
          if (data?.code === "signup_profile_save_failed") {
            return (
              data.error ||
              t("signup_profile_save_failed") ||
              "Your login was created but the application could not be saved. Please try again."
            );
          }
          return data?.error || "Server error. Please try again later.";
        default:
          return data.error || "An error occurred. Please try again.";
      }
    } else if (error.request) {
      return t("signup_network_error");
    } else {
      return error.message || t("signup_unexpected_error");
    }
  };

  // Loading message helper
  const getLoadingMessage = () => {
    switch (loadingStep) {
      case 'validating':
        return 'Validating information...';
      case 'registering':
        return 'Creating your account...';
      case 'creating-profile':
        return 'Setting up your profile...';
      case 'uploading-image':
        return t("signup_uploading_picture");
      default:
        return 'Creating account...';
    }
  };

  return (
    <>
      <CssBaseline />
      <Box sx={{ ...signupPageOuterSx(isDarkMode), position: "relative", overflow: "hidden" }}>
        <AuthPageBottomAnimation isDarkMode={isDarkMode} />
        <AuthPageToggleBar />
        <Container maxWidth={false} sx={signupPageContainerSx}>
          <Fade in timeout={500}>
            <Box sx={signupContentColumnSx}>
              <Box sx={{ textAlign: "center", mb: { xs: 2, sm: 3 } }}>
                <Box
                  component="img"
                  src={isDarkMode ? "NewEraLogo.png" : "NewEraBlackLogo.png"}
                  alt="NewEra Logo"
                  sx={{ ...logoSx, mb: 1.5 }}
                />
                <Typography sx={typeSx.subtitle}>
                  {t("signup_join_community")}
                </Typography>
              </Box>

              {inviteLinkInvalid && (
                <Alert severity="warning" sx={{ mb: 2, ...signupAlertSx(authAlertWarningSx, isDarkMode) }}>
                  {t("signup_invite_invalid")}
                </Alert>
              )}

              {inviteClubLocked && form.clubname && (
                <Alert severity="info" sx={{ mb: 2, ...signupAlertSx(authAlertInfoSx, isDarkMode) }}>
                  {t("signup_invite_club_prefilled", { club: form.clubname })}
                </Alert>
              )}

              {inviteRoleLocked && role && (
                <Alert severity="info" sx={{ mb: 2, ...signupAlertSx(authAlertInfoSx, isDarkMode) }}>
                  {t("signup_invite_role_prefilled", {
                    role:
                      role === "Parent"
                        ? t("signup_i_am_parent")
                        : role === "Student"
                          ? t("signup_i_am_student")
                          : t("signup_i_am_both"),
                  })}
                </Alert>
              )}

              <Card
                ref={wizardAnchorRef}
                sx={{
                  ...cardShellSx,
                  mb: { xs: 2, sm: 3 },
                  scrollMarginTop: {
                    xs: "calc(env(safe-area-inset-top, 0px) + 64px)",
                    sm: "calc(env(safe-area-inset-top, 0px) + 80px)",
                  },
                }}
              >
                <CardContent sx={signupCardContentSx}>
                  <SignupStepIndicator
                    steps={wizardStepLabels}
                    activeIndex={wizardStep}
                    isDarkMode={isDarkMode}
                  />

                  <Box sx={{ overflow: "hidden", width: "100%" }}>
                    <SignupStepTransition stepKey={currentWizardKey} direction={slideDirection}>
                      {currentWizardKey === "role" && (
                        <SignupRoleSelector
                          role={role}
                          onSelect={handleRoleSelection}
                          isDarkMode={isDarkMode}
                        />
                      )}

                      {currentWizardKey === "instructions" && (
                        <Box>
                          <Typography sx={{ ...typeSx.brandTitle, mb: 3, textAlign: "center", fontSize: { xs: "1.375rem", sm: "1.5rem" }, fontWeight: 700 }}>
                            {t("signup_welcome")}
                          </Typography>

                    <Box sx={{ mb: 3 }}>
                      <Typography sx={{ ...typeSx.sectionTitle, mb: 1.5 }}>
                        {t("signup_before_register")}
                      </Typography>
                      <Typography sx={{ ...typeSx.body, mb: 2 }}>
                        {t("signup_read_carefully")}
                      </Typography>
                    </Box>

                    <Box sx={{ ...signupSummaryBoxSx(isDarkMode), mb: 3 }}>
                      <Typography sx={{ ...typeSx.sectionTitle, mb: 1.5, fontSize: "1rem" }}>
                        {t("signup_important_notes")}
                      </Typography>
                      <Box component="ul" sx={{
                        pl: 3,
                        m: 0,
                        "& li": { ...typeSx.body, mb: 1.25 },
                      }}>
                        <li>
                          <strong style={{ color: getMm(isDarkMode).onSurface, fontFamily: SIGNUP_FONT }}>{t("signup_remember")}</strong> {t("signup_remember_details")}
                        </li>
                        <li>
                          <strong style={{ color: getMm(isDarkMode).onSurface, fontFamily: SIGNUP_FONT }}>{t("signup_students_16")}</strong> {t("signup_students_16_details")}
                        </li>
                        <li>{t("signup_info_accurate")}</li>
                        <li>{t("signup_picture_clear")}</li>
                        <li>{t("signup_password_secure")}</li>
                        <li>{t("signup_parent_add_children")}</li>
                      </Box>
                    </Box>

                    <Box sx={{ mb: 3 }}>
                      <Typography sx={{ ...typeSx.sectionTitle, mb: 1.5 }}>
                        {t("signup_registration_process")}
                      </Typography>
                      <Box component="ol" sx={{
                        pl: 3,
                        mb: 2,
                        "& li": { ...typeSx.body, mb: 1.5 },
                      }}>
                        <li>
                          <strong style={{ color: getMm(isDarkMode).onSurface, fontFamily: SIGNUP_FONT }}>{t("signup_fill_form")}</strong> {t("signup_fill_form_details")}
                        </li>
                        <li>
                          <strong style={{ color: getMm(isDarkMode).onSurface, fontFamily: SIGNUP_FONT }}>{t("signup_upload_picture")}</strong> - {t("signup_upload_picture_details")}
                        </li>
                        <li>
                          <strong style={{ color: getMm(isDarkMode).onSurface, fontFamily: SIGNUP_FONT }}>{t("signup_select_sport")}</strong> - {t("signup_select_sport_details")}
                        </li>
                        <li>
                          <strong style={{ color: getMm(isDarkMode).onSurface, fontFamily: SIGNUP_FONT }}>{t("signup_select_belt")}</strong> - {t("signup_select_belt_details")}
                        </li>
                        <li>
                          <strong style={{ color: getMm(isDarkMode).onSurface, fontFamily: SIGNUP_FONT }}>{t("signup_submit")}</strong> - {t("signup_submit_details")}
                        </li>
                      </Box>
                    </Box>

                    <Box sx={{ mb: 3 }}>
                      <Typography sx={{ ...typeSx.sectionTitle, mb: 1.5 }}>
                        {t("signup_review_process")}
                      </Typography>
                      <Box component="ul" sx={{
                        pl: 3,
                        mb: 2,
                        "& li": { ...typeSx.body, mb: 1.5 },
                      }}>
                        <li>{t("signup_review_1")}</li>
                        <li>{t("signup_review_2")}</li>
                        <li>{t("signup_review_3")}</li>
                        <li>{t("signup_review_4")}</li>
                      </Box>
                    </Box>

                    <Box sx={{
                      display: "flex",
                      justifyContent: "center",
                      alignItems: "center",
                      pt: 2,
                      borderTop: `2px solid ${getMm(isDarkMode).outlineVariant}`,
                    }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={instructionsAcknowledged}
                            onChange={(e) => setInstructionsAcknowledged(e.target.checked)}
                            sx={{
                              color: getMm(isDarkMode).onSurfaceVariant,
                              "&.Mui-checked": { color: BRAND_PRIMARY.main },
                            }}
                          />
                        }
                        label={
                          <Typography sx={{ ...typeSx.body, color: getMm(isDarkMode).onSurface, fontWeight: 500 }}>
                            {t("signup_i_understand")}
                          </Typography>
                        }
                        sx={{ m: 0 }}
                      />
                    </Box>
                        </Box>
                      )}

                      {currentWizardKey === "form" && (
                        <Box>
                    <Box sx={{ textAlign: "center", mb: 3 }}>
                      <Typography sx={{ ...typeSx.brandTitle, mb: 0.75, fontSize: { xs: "1.375rem", sm: "1.5rem" }, fontWeight: 700 }}>
                        {t("signup_create_account")}
                      </Typography>
                      <Typography sx={typeSx.subtitle}>
                        {t("signup_join_community")}
                      </Typography>
                    </Box>

                    <Alert
                      severity="info"
                      sx={{
                        mb: 2.5,
                        ...signupAlertSx(authAlertInfoSx, isDarkMode),
                        "& .MuiAlert-message": { width: "100%" },
                      }}
                    >
                      <Typography component="div" sx={{ fontWeight: 600, fontSize: "0.875rem", mb: 0.5, fontFamily: SIGNUP_FONT }}>
                        {t("signup_help_banner_title")}
                      </Typography>
                      <Typography component="div" sx={{ ...typeSx.body, fontSize: "0.8125rem" }}>
                        {t("signup_help_banner_body")}
                      </Typography>
                    </Alert>

                    {duplicateEmailGuidance && (
                      <Alert
                        severity="warning"
                        sx={{ mb: 2.5, ...signupAlertSx(authAlertWarningSx, isDarkMode) }}
                      >
                        <Typography sx={{ ...typeSx.body, mb: 1 }}>
                          {t("signup_duplicate_guidance_message")}
                        </Typography>
                        <Link to="/reset-password" style={{ color: "inherit", fontWeight: 600, fontFamily: SIGNUP_FONT }}>
                          {t("signup_forgot_password_reset_link")}
                        </Link>
                        {" · "}
                        <Link to="/login" style={{ color: "inherit", fontWeight: 600, fontFamily: SIGNUP_FONT }}>
                          {t("signup_sign_in_to_account")}
                        </Link>
                      </Alert>
                    )}

                    <Box component="form" sx={{ mb: { xs: 2, sm: 3 }, width: "100%" }}>
                      <Grid container spacing={{ xs: 1.5, sm: 2.5 }} sx={signupFormGridSx}>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label={t("signup_email_address")}
                            type="email"
                            variant="outlined"
                            name="email"
                            value={form.email}
                            onInput={onFormInputChange}
                            required
                            fullWidth
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <EmailIcon sx={{ color: getMm(isDarkMode).onSurfaceVariant }} />
                                </InputAdornment>
                              ),
                            }}
                            sx={fieldSx}
                          />
                        </Grid>
                        <Grid item xs={12} md={6}>
                          <TextField
                            label={t("signup_password")}
                            type={showPassword ? "text" : "password"}
                            variant="outlined"
                            name="password"
                            value={form.password}
                            onInput={onFormInputChange}
                            required
                            fullWidth
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <LockIcon sx={{ color: getMm(isDarkMode).onSurfaceVariant }} />
                                </InputAdornment>
                              ),
                              endAdornment: (
                                <InputAdornment position="end">
                                  <IconButton
                                    onClick={handleTogglePasswordVisibility}
                                    edge="end"
                                    sx={{ color: getMm(isDarkMode).onSurfaceVariant }}
                                  >
                                    {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                                  </IconButton>
                                </InputAdornment>
                              ),
                            }}
                            sx={fieldSx}
                          />
                        </Grid>
                        <Grid item xs={12} md={signupPersonnummerRequired ? 6 : 12}>
                          <TextField
                            label={t("signup_full_name")}
                            name="name"
                            value={form.name}
                            onInput={onFormInputChange}
                            required
                            fullWidth
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <PersonIcon sx={{ color: getMm(isDarkMode).onSurfaceVariant }} />
                                </InputAdornment>
                              ),
                            }}
                            sx={fieldSx}
                          />
                        </Grid>
                        {signupPersonnummerRequired ? (
                          <Grid item xs={12} md={6} sx={{ width: "100%" }}>
                            <TextField
                              label={t("personnummer")}
                              name="personnummer"
                              value={form.personnummer}
                              onInput={onFormInputChange}
                              required
                              fullWidth
                              autoComplete="off"
                              helperText={t("personnummer_helper")}
                              error={
                                Boolean(form.personnummer?.trim()) &&
                                !isPersonnummerPlausible(form.personnummer)
                              }
                              sx={fieldSx}
                            />
                          </Grid>
                        ) : null}
                        <Grid item xs={12}>
                          <TextField
                            label={t("address")}
                            type="text"
                            variant="outlined"
                            name="address"
                            multiline
                            maxRows={4}
                            value={form.address}
                            onInput={onFormInputChange}
                            fullWidth
                            helperText={t("signup_address_optional")}
                            InputProps={{
                              startAdornment: (
                                <InputAdornment position="start">
                                  <LocationIcon sx={{ color: getMm(isDarkMode).onSurfaceVariant }} />
                                </InputAdornment>
                              ),
                            }}
                            sx={fieldSx}
                          />
                        </Grid>
                        <Grid item xs={12}>
                          <InputLabel id="club-label" sx={typeSx.label}>
                            {t("signup_club_name")}
                          </InputLabel>
                          <Select
                            labelId="club-label"
                            id="club"
                            name="clubID"
                            value={form.clubID}
                            onChange={handleClubChange}
                            label={t("signup_club_name")}
                            fullWidth
                            disabled={clubsLoading || !!clubsError || inviteClubLocked}
                            sx={selectSx}
                          >
                            {clubsLoading && (
                              <MenuItem value="" disabled>
                                Loading academies…
                              </MenuItem>
                            )}
                            {!clubsLoading && clubsError && (
                              <MenuItem value="" disabled>
                                {clubsError}
                              </MenuItem>
                            )}
                            {!clubsLoading && !clubsError && clubs.length === 0 && (
                              <MenuItem value="" disabled>
                                No academies available
                              </MenuItem>
                            )}
                            {clubs.map((club) => (
                              <MenuItem key={club.clubID} value={String(club.clubID)}>
                                {club.clubname}
                              </MenuItem>
                            ))}
                          </Select>
                        </Grid>

                        {isParentSignupRole && (
                          <Grid item xs={12}>
                            <TextField
                              name="childName"
                              label={t("signup_child_name")}
                              value={form.childName}
                              onChange={onFormInputChange}
                              fullWidth
                              required
                              placeholder={t("signup_child_name_placeholder")}
                              helperText={t("signup_child_name_parent_hint")}
                              sx={fieldSx}
                            />
                          </Grid>
                        )}

                        {showSportField && (
                          <Grid item xs={12}>
                            <InputLabel id="sport-label" sx={typeSx.label}>
                              {t("signup_sport_label")}
                            </InputLabel>
                            <Select
                              labelId="sport-label"
                              id="sportKey"
                              name="sportKey"
                              value={form.sportKey}
                              onChange={handleSportChange}
                              label={t("signup_sport_label")}
                              fullWidth
                              required
                              sx={selectSx}
                            >
                              <MenuItem value="" disabled>
                                {t("signup_sport_placeholder")}
                              </MenuItem>
                              {signupSportOptions.map((opt) => (
                                <MenuItem key={opt.value} value={opt.value}>
                                  {t(opt.labelKey)}
                                </MenuItem>
                              ))}
                            </Select>
                          </Grid>
                        )}

                        {showBeltField && (
                          <Grid item xs={12}>
                            <PromotionBeltPickerField
                              value={resolvePromotionBeltPickerValue(form.belt, signupBeltOrder)}
                              beltOrder={signupBeltOrder}
                              beltSystemKey={signupBeltSystemKey}
                              formatBeltName={(belt) => getBeltDisplayName(belt, t)}
                              onChange={handleSignupBeltChange}
                              disabled={isLoading}
                              isDarkMode={isDarkMode}
                              placeholder={t("signup_belt_placeholder")}
                              label={t("belt")}
                              changeHint={t("promotion_belt_click_to_change")}
                              fieldVariant="form"
                              visualVariant="prominent"
                              allowAnyBelt
                              fullWidth
                            />
                          </Grid>
                        )}

                        {/* Profile Picture Upload */}
                        <Grid item xs={12}>
                          <Box sx={{
                            display: "flex",
                            flexDirection: "column",
                            alignItems: "center",
                            gap: 2,
                            p: 2.5,
                            border: `1px solid ${getMm(isDarkMode).outlineVariant}`,
                            borderRadius: "8px",
                            backgroundColor: getMm(isDarkMode).surfaceLow,
                            boxShadow: "none",
                            fontFamily: SIGNUP_FONT,
                          }}>
                            <Typography sx={{ ...typeSx.sectionTitle, textAlign: "center", fontSize: "1rem" }}>
                              {t("signup_profile_picture")} *
                            </Typography>
                            <Typography sx={{ ...typeSx.body, textAlign: "center", maxWidth: "400px" }}>
                              {t("signup_upload_photo_instruction")}
                            </Typography>
                            
                            {/* Image Preview */}
                            {imagePreview ? (
                              <Avatar
                                src={imagePreview}
                                sx={{ 
                                  width: 120, 
                                  height: 120,
                                  border: `1px solid ${getMm(isDarkMode).outlineVariant}`,
                                  boxShadow: 'none'
                                }}
                              />
                            ) : (
                              <Avatar sx={{ 
                                width: 120, 
                                height: 120,
                                backgroundColor: getMm(isDarkMode).surfaceLow,
                                border: `1px solid ${getMm(isDarkMode).outlineVariant}`
                              }}>
                                <PhotoCameraIcon sx={{ fontSize: 40, color: getMm(isDarkMode).onSurfaceVariant }} />
                              </Avatar>
                            )}

                            {/* Upload Button */}
                            <Button
                              variant="outlined"
                              component="label"
                              startIcon={<PhotoCameraIcon />}
                              disabled={isCompressing}
                              sx={{ ...outlineBtnSx, justifyContent: "center" }}
                            >
                              {isCompressing ? t("signup_compressing") : (imagePreview ? t("signup_change_picture") : t("signup_upload_picture_label"))}
                              <input
                                type="file"
                                accept="image/*"
                                onChange={handleImageChange}
                                style={{ display: 'none' }}
                              />
                            </Button>

                            {isCompressing && (
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                <CircularProgress size={16} />
                                <Typography sx={{ ...typeSx.body }}>
                                  {t("signup_compressing_image")}
                                </Typography>
                              </Box>
                            )}
                          </Box>
                        </Grid>

                      </Grid>
                    </Box>
                        </Box>
                      )}
                    </SignupStepTransition>
                  </Box>

                  <Box sx={{ mt: 3, display: "flex", flexDirection: "column", gap: 1.25 }}>
                    {currentWizardKey === "role" && (
                      <Button
                        variant="contained"
                        fullWidth
                        disabled={!role}
                        onClick={goNextWizardStep}
                        endIcon={<ArrowForwardIcon />}
                        sx={{ ...primaryBtnSx, py: 1.5 }}
                      >
                        {t("signup_continue")}
                      </Button>
                    )}

                    {currentWizardKey === "instructions" && (
                      <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 1.25 }}>
                        {showRoleStep && (
                          <Button
                            variant="outlined"
                            fullWidth
                            onClick={goBackWizardStep}
                            startIcon={<ArrowBackIcon />}
                            sx={{ ...outlineBtnSx, py: 1.5, flex: 1 }}
                          >
                            {t("back")}
                          </Button>
                        )}
                        <Button
                          variant="contained"
                          fullWidth
                          disabled={!instructionsAcknowledged}
                          onClick={goNextWizardStep}
                          endIcon={<ArrowForwardIcon />}
                          sx={{ ...primaryBtnSx, py: 1.5, flex: 1 }}
                        >
                          {t("signup_continue")}
                        </Button>
                      </Box>
                    )}

                    {currentWizardKey === "form" && (
                      <>
                        <Box sx={{ display: "flex", flexDirection: { xs: "column", sm: "row" }, gap: 1.25 }}>
                          <Button
                            variant="outlined"
                            fullWidth
                            onClick={goBackWizardStep}
                            startIcon={<ArrowBackIcon />}
                            sx={{ ...outlineBtnSx, py: 1.5, flex: 1 }}
                          >
                            {t("back")}
                          </Button>
                          <Button
                            variant="contained"
                            onClick={onSubmit}
                            disabled={!signupSubmitReady}
                            fullWidth
                            startIcon={isLoading ? <CircularProgress size={20} color="inherit" /> : null}
                            sx={{ ...primaryBtnSx, py: 1.5, flex: 1 }}
                          >
                            {isLoading ? getLoadingMessage() : t("signup_create_account")}
                          </Button>
                        </Box>

                        <Divider sx={{ my: 0.5, borderColor: getMm(isDarkMode).outlineVariant }}>
                          <Typography sx={{ ...typeSx.subtitle, px: 2 }}>
                            {t("signup_already_have_account")}
                          </Typography>
                        </Divider>

                        <Box sx={{ display: "flex", flexDirection: "column", gap: 1.25 }}>
                          <Button
                            component={Link}
                            to="/login"
                            variant="outlined"
                            fullWidth
                            startIcon={<LoginIcon />}
                            sx={{ ...outlineBtnSx, justifyContent: "flex-start" }}
                          >
                            {t("signup_sign_in_to_account")}
                          </Button>
                          <Button
                            component={Link}
                            to="/reset-password"
                            variant="outlined"
                            fullWidth
                            startIcon={<LockResetIcon />}
                            sx={{ ...outlineBtnSx, justifyContent: "flex-start" }}
                          >
                            {t("signup_forgot_password")}
                          </Button>
                        </Box>
                      </>
                    )}
                  </Box>
                  </CardContent>
                </Card>
            </Box>
          </Fade>
        </Container>
      </Box>
      
      {showEmailConfirmationPopup && (
        <Box
          sx={{
            position: "fixed",
            inset: 0,
            backgroundColor: "rgba(17, 24, 39, 0.72)",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            zIndex: 9999,
            p: 2,
          }}
        >
          <Card
            sx={{
              ...cardShellSx,
              maxWidth: { xs: "100%", sm: 560, md: 680, lg: 760 },
              width: "100%",
              textAlign: "center",
            }}
          >
            <CardContent sx={{ p: { xs: 3, sm: 4 }, position: "relative" }}>
              <IconButton
                onClick={() => navigate("/")}
                sx={{
                  position: "absolute",
                  top: 12,
                  right: 12,
                  color: getMm(isDarkMode).onSurfaceVariant,
                  boxShadow: "none",
                  "&:hover": { backgroundColor: getMm(isDarkMode).surfaceContainer },
                }}
              >
                <CloseIcon />
              </IconButton>

              <Box
                sx={{
                  width: 72,
                  height: 72,
                  borderRadius: "8px",
                  backgroundColor: BRAND_PRIMARY.main,
                  border: `2px solid ${BRAND_PRIMARY.dark}`,
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                  mx: "auto",
                  mb: 2,
                  boxShadow: "none",
                }}
              >
                <CheckCircleIcon sx={{ fontSize: 36, color: "white" }} />
              </Box>

              <Typography sx={{ ...typeSx.sectionTitle, mb: 1.5 }}>
                {t("registration_successful")}
              </Typography>

              <Typography sx={{ ...typeSx.body, mb: 2.5 }}>
                {t("account_created_awaiting_approval")}
              </Typography>

              <Box sx={{ ...signupSummaryBoxSx(isDarkMode), mb: 3, textAlign: "left" }}>
                <Typography
                  sx={{
                    color: isDarkMode ? getMm(isDarkMode).onSurface : BRAND_PRIMARY.main,
                    fontWeight: 600,
                    mb: 1,
                    fontSize: "0.875rem",
                    fontFamily: SIGNUP_FONT,
                  }}
                >
                  {t("admin_approval_notice")}
                </Typography>
                <Typography sx={typeSx.body}>
                  {t("email_when_approved")}
                </Typography>
              </Box>

              <Button
                variant="contained"
                size="large"
                fullWidth
                onClick={() => navigate("/")}
                sx={{ ...primaryBtnSx, py: 1.5 }}
              >
                {t("access_system")}
              </Button>
            </CardContent>
          </Card>
        </Box>
      )}
      <SnackbarNotification
        open={snackbar.open}
        message={snackbar.message}
        severity={snackbar.severity}
        onClose={() => setSnackbar(s => ({ ...s, open: false }))}
      />
    </>
  );
};

const Signup = () => {
  return (
    <CustomThemeProvider>
      <SignupContent />
    </CustomThemeProvider>
  );
};

export default Signup;